import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { reservationService, destinationService, pricingService } from '../../api/services';
import PriceSearchLoading from './PriceSearchLoading';
import PriceInquiryModal from './PriceInquiryModal';
import LoginPromptModal from './LoginPromptModal';
import ReservationWaitingModal from './ReservationWaitingModal';

/**
 * SearchFlow: Wraps the search button flow for all transfer pages.
 *
 * Corrected Flow (matching Figma):
 * 1. Click 검색 → LoadingOverlay (2.5s animated progress)
 * 2. After loading → PriceInquiryModal (차량 정보 with specs + price)
 * 3. Click 선택 → check auth
 *    - If not logged in → LoginPromptModal (회원가입 / 로그인 / 비회원 예약)
 * 4. After auth OK → check availability
 *    - If date fully booked (simulated 20%) → ReservationWaitingModal
 *    - If available → navigate to /transfer/{type}/booking
 *    - If waiting registered → navigate to /transfer/{type}/booking?waiting=true
 */
export function useSearchFlow(type) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // null | 'loading' | 'price' | 'login' | 'waiting'
  const [modal, setModal] = useState(null);
  const [loadingDone, setLoadingDone] = useState(false);
  const [waitingDate, setWaitingDate] = useState('');
  const [savedDateTime, setSavedDateTime] = useState('');
  const extraParamsRef = useRef({});
  const formDataRef = useRef({});
  const vehicleDataRef = useRef(null);
  const searchTimerRef = useRef(null);
  const cancelledRef = useRef(false);
  const bufferSettingsRef = useRef([]);

  // Load buffer settings for end_time calculation
  useEffect(() => {
    destinationService.getBufferSettings(type).then((data) => {
      bufferSettingsRef.current = data;
    }).catch(() => {});
  }, [type]);

  // Clear timer on unmount
  useEffect(() => () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); }, []);

  const startSearch = useCallback(async (dateTime, params = {}, formData = {}) => {
    setSavedDateTime(dateTime);
    extraParamsRef.current = params;
    formDataRef.current = { ...formData, dateTime };
    vehicleDataRef.current = null;
    cancelledRef.current = false;
    setLoadingDone(false);
    setModal('loading');

    // 가격 검색 요청 body — 값이 있는 필드만 포함
    const [datePart, timePart] = (dateTime || '').split('T');
    const priceBody = { service_type: type };
    if (datePart) priceBody.date = datePart;
    if (timePart) priceBody.pickup_time = timePart;
    if (formData.people) priceBody.passenger_count = Number(formData.people);
    /* BugHerd #85: 외국어 모드/명칭만 입력된 케이스에서 BE longest-match 가 시·도·구
       키워드를 못 찾아 fallback(198k) 으로 잘못 청구되던 회귀. address + name 을 합친
       값으로 destination 보내면 한국어 주소 키워드가 항상 포함되어 매칭 안정화.
       departure 도 같은 패턴 적용 (도착지 검색에 명칭만 입력하는 케이스). */
    const joinAddrName = (addr, name) => [addr, name].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' ').trim();
    const departureCombined = joinAddrName(formData.departure, formData.departureName);
    if (departureCombined) priceBody.departure_region = departureCombined;

    /* BugHerd #38: golf/ski 는 BE 가 Golf.name / Ski.name 매칭으로 골프장별
       개별 가격(golf_pricing)을 적용함. destination(고객 도착지 주소)이 아니라
       venueName(골프장명) 을 destination_region 으로 보내야 적중. BookingSettings 와 동일 규칙.
       BugHerd #98: hospital 은 HospitalRegion.name(서울/경기 등) 으로 매칭. hospital full name
       이나 도착지 주소로 보내면 region 매칭 실패 → 기본 fallback 가격(99,900원) 반환. */
    const fixedWaypoint = type === 'golf' || type === 'ski';
    let pricingDest;
    if (type === 'hospital' && formData.hospitalRegion) {
      pricingDest = formData.hospitalRegion;
    } else if (fixedWaypoint) {
      // #217: BE Golf.name/Ski.name 은 한글 정확매칭 → 번역본(venueName) 대신 원본 한글명(venueNameKo) 우선.
      //   영어 등 외국어 진입 시 venueName 이 번역돼 매칭 실패 → 기본가 폴백되던 회귀 차단.
      pricingDest = formData.venueNameKo || formData.venueName || formData.venueAddress || formData.destination;
    } else {
      pricingDest = joinAddrName(formData.destination, formData.destinationName);
    }
    if (pricingDest) priceBody.destination_region = pricingDest;

    // 최소 2.5초 + API 응답, 둘 다 끝날 때까지 대기
    const minDelay = new Promise(resolve => setTimeout(resolve, 2500));
    try {
      const [vehicleInfo, priceResult] = await Promise.all([
        pricingService.getVehicleInfo(type),
        pricingService.search(priceBody).catch((err) => {
          // 가격 검색 실패는 무시하지 않음 — 0원 회귀가 클라이언트에 보고된 원인.
          // BE 에러를 표면화해서 운영팀이 즉시 인지하게 함 (DB 마이그레이션 누락 등).
          console.error('[SearchFlow] /api/price/search 실패:', err?.response?.status, err?.message);
          return null;
        }),
        minDelay,
      ]);
      // BugHerd #36 (재발): /api/price/config/{type} 은 direction='pickup' 고정이라
      // 공항 샌딩(to-airport) 검색 시에도 픽업 default 단가/대기시간이 모달에 표시됨.
      // /api/price/search 응답 service_info 는 detect_airport_direction 으로 direction-aware
      // 값을 내려주므로, 그 값을 우선 적용해 vehicleInfo.waitInfo 를 덮어씀.
      const merged = { ...vehicleInfo };
      const svcInfo = priceResult?.service_info;
      if (svcInfo && type === 'airport' && svcInfo.overtime_price != null) {
        // overtimePrice 를 raw number 로 전달 → VehicleInfoSection 이 useMoney + i18n 으로 포맷.
        // (이전엔 한국어 문자열로 사전 포맷해 DeepL 로 보냈더니 통화는 KRW 박힌 채 단어만 번역되던 회귀)
        // #211: 공항은 "초과 대기 요금" 라벨 사용 → overtime_waiting_fee_per_hour 키 명시.
        merged.waitInfo = {
          ...(vehicleInfo.waitInfo || {}),
          overtimePrice: Number(svcInfo.overtime_price),
          overtimeI18nKey: 'booking.overtime_waiting_fee_per_hour',
        };
      }
      vehicleDataRef.current = {
        ...merged,
        basePrice: Number(priceResult?.base_price ?? priceResult?.total ?? 0),
        // #35: BE 가 holiday/weekend/night 합산해서 surcharges_total 로 내려줌.
        //      PriceInquiryModal 은 이 값을 base 에 합산해서 예상 결제액으로 노출.
        surchargesTotal: Number(priceResult?.surcharges_total ?? 0),
        // priceResult 가 없으면 BE 호출이 실패한 케이스. 모달에서 0원 대신 안내 문구 노출.
        priceUnavailable: priceResult == null,
      };
    } catch {
      await minDelay.catch(() => {});
    }

    // 사용자가 돌아가기를 눌렀으면 모달 전환하지 않음
    if (cancelledRef.current) return;
    // 90% → 100% 애니메이션 후 모달 전환 (300ms)
    setLoadingDone(true);
    searchTimerRef.current = setTimeout(() => {
      if (!cancelledRef.current) setModal('price');
    }, 300);
  }, [type]);

  const buildBookingUrl = useCallback((extra = {}) => {
    const params = new URLSearchParams({ ...extraParamsRef.current, ...extra });
    const qs = params.toString();
    return `/transfer/${type}/booking${qs ? `?${qs}` : ''}`;
  }, [type]);

  const checkAvailability = useCallback(async () => {
    try {
      // savedDateTime: "2026-03-20T06:00" 형식 파싱
      const [datePart, timePart] = (savedDateTime || '').split('T');

      // 서비스 direction에 맞는 base_driving_minutes 조회
      const direction = extraParamsRef.current.direction || 'base';
      const bufferEntry = bufferSettingsRef.current.find(
        (b) => b.direction === direction
      ) || bufferSettingsRef.current[0];
      const baseDrivingMinutes = bufferEntry?.base_driving_minutes ?? 60; // fallback: 60분

      // end_time = start_time + base_driving_minutes
      let endTime;
      if (timePart && baseDrivingMinutes != null) {
        const [h, m] = timePart.split(':').map(Number);
        const total = h * 60 + m + baseDrivingMinutes;
        endTime = `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
      }

      const availParams = { service_type: type };
      if (datePart) availParams.date = datePart;
      if (timePart) availParams.start_time = timePart;
      if (endTime || timePart) availParams.end_time = endTime || timePart;
      if (formDataRef.current.people) availParams.passengers = formDataRef.current.people;

      const result = await reservationService.checkAvailability(availParams);
      if (result?.available === false) {
        setWaitingDate(savedDateTime || '');
        setModal('waiting');
      } else {
        setModal(null);
        navigate(buildBookingUrl(), { state: { formData: formDataRef.current } });
      }
    } catch {
      // API 실패 시 기본적으로 예약 가능 처리
      setModal(null);
      navigate(buildBookingUrl(), { state: { formData: formDataRef.current } });
    }
  }, [type, navigate, savedDateTime, buildBookingUrl]);

  const handlePriceSelect = useCallback(() => {
    // Step 3: Check auth
    if (!user) {
      setModal('login');
      return;
    }
    // Step 4: Check availability
    checkAvailability();
  }, [user, checkAvailability]);

  const handleGuestBooking = useCallback(() => {
    setModal(null);
    // Guest proceeds to availability check
    checkAvailability();
  }, [checkAvailability]);

  const saveSearchToSession = useCallback(() => {
    try {
      const data = {
        serviceType: type,
        ...formDataRef.current,
        ...extraParamsRef.current,
      };
      sessionStorage.setItem('pendingSearchData', JSON.stringify(data));
    } catch (e) {
      console.warn('[SearchFlow] sessionStorage 저장 실패:', e.message);
    }
  }, [type]);

  const handleLoginNavigate = useCallback(() => {
    setModal(null);
  }, []);

  const closeModal = useCallback(() => {
    cancelledRef.current = true;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setModal(null);
  }, []);

  const handleSelectOtherDate = useCallback(() => {
    setModal(null);
  }, []);

  const handleRegisterWaiting = useCallback(async () => {
    setModal(null);
    // 대기 등록 → booking 페이지(waiting 모드)로 이동 → 결제 없이 대기 완료 페이지로
    navigate(buildBookingUrl({ waiting: 'true' }), { state: { formData: formDataRef.current } });
  }, [navigate, buildBookingUrl]);

  return {
    modal,
    loadingDone,
    vehicleDataRef,
    waitingDate,
    startSearch,
    handlePriceSelect,
    handleGuestBooking,
    handleLoginNavigate,
    closeModal,
    handleSelectOtherDate,
    handleRegisterWaiting,
    saveSearchToSession,
    type,
  };
}

/**
 * SearchFlowModals: Renders the appropriate modal based on flow state.
 * Place this at the bottom of your transfer page component.
 */
export function SearchFlowModals({ flow, dateTime }) {
  const {
    modal, loadingDone, vehicleDataRef, waitingDate, closeModal, type,
    handlePriceSelect, handleGuestBooking, saveSearchToSession,
    handleSelectOtherDate, handleRegisterWaiting,
  } = flow;

  return (
    <>
      {modal === 'loading' && (
        <PriceSearchLoading onClose={closeModal} done={loadingDone} />
      )}
      {modal === 'price' && (
        <PriceInquiryModal
          type={type}
          vehicleData={vehicleDataRef.current}
          dateTime={dateTime || ''}
          onClose={closeModal}
          onSelect={handlePriceSelect}
        />
      )}
      {modal === 'login' && (
        <LoginPromptModal
          onClose={closeModal}
          onGuestBooking={handleGuestBooking}
          onBeforeLogin={saveSearchToSession}
        />
      )}
      {modal === 'waiting' && (
        <ReservationWaitingModal
          date={waitingDate}
          onClose={closeModal}
          onSelectOtherDate={handleSelectOtherDate}
          onRegisterWaiting={handleRegisterWaiting}
        />
      )}
    </>
  );
}
